import type { OtimAPIClient } from "@api/api-client";
import type {
  DelegationCreateRequest,
  DelegationStatusResponse,
} from "@api/schemas/delegation";
import type { Address, WalletClient } from "viem";

export interface DelegationStatusOptions {
  address?: Address;
  chainId?: number;
}

export interface DelegateOptions {
  address?: Address;
  signedAuthorization: string;
}

async function createDelegation(
  client: OtimAPIClient,
  signedAuthorization: string,
  signerAddress: Address,
) {
  const delegationRequest: DelegationCreateRequest = {
    signedAuthorization,
    signerAddress,
  };
  return client.delegation.create(delegationRequest);
}

export async function getDelegationStatus(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { address, chainId }: DelegationStatusOptions = {},
): Promise<DelegationStatusResponse> {
  if (!walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = address || walletClient.account.address;
  const targetChainId = chainId || walletClient.chain.id;

  const response = await client.delegation.getStatus({
    address: targetAddress,
    chainId: targetChainId,
  });
  return response.data;
}

export async function delegate(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { address, signedAuthorization }: DelegateOptions,
) {
  if (!walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = address || walletClient.account.address;

  const delegation = await createDelegation(
    client,
    signedAuthorization,
    targetAddress,
  );

  return delegation;
}

export interface DelegationClient {
  readonly getDelegationStatus: (
    options: DelegationStatusOptions,
  ) => ReturnType<typeof getDelegationStatus>;
  readonly delegate: (options: DelegateOptions) => ReturnType<typeof delegate>;
}

export function createDelegationClient(
  client: OtimAPIClient,
  walletClient: WalletClient,
): DelegationClient {
  return {
    getDelegationStatus: (options: DelegationStatusOptions = {}) =>
      getDelegationStatus(client, walletClient, options),
    delegate: (options: DelegateOptions) =>
      delegate(client, walletClient, options),
  };
}
